<template>
    <FormSection
        title="Demographic Information"
        type="his-section"
        :full-height="false"
        :application-id="referral?.application"
    >
        <template v-slot:content>
            <section class="row py-3 pb-0 g-0">
                <div class="col-12 col-lg-6 px-4 col-md-12 g-0">
                    <div class="col-12 py-2">
                        <div class="w-100">
                            <h6 class="mb-2 d-flex align-items-center">
                                <b class="me-2">Patient Information</b>
                            </h6>
                        </div>
                        <div class="row">
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_First_Name_Label" for="referral-first-name">
                                    <span class="badge me-1">M0040</span> First Name
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    id="Referral_First_Name"
                                    name="referral-first-name"
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter First Name"
                                    v-model="referral.Referral.FirstName"
                                    @blur="v$.referral.Referral.FirstName.$touch"
                                    :class="`${!v$.referral.Referral.FirstName.$error ? '' : 'field-invalid'}`"
                                />
                                <span v-if="v$.referral.Referral.FirstName.$error" class="field-invalid"
                                    >Firstname is required.</span
                                >
                            </div>
                            <div class="form-group col-4 col-lg-6 pe-lg-2">
                                <label for="referral-middle-initial">
                                    <span class="badge me-1">M0040</span> M.I.
                                </label>
                                <input
                                    name="referral-middle-initial"
                                    class="form-control"
                                    maxlength="1"
                                    type="text"
                                    placeholder="Enter M.I."
                                    v-model="referral.Referral.MiddleInitial"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Last_Name_Label" for="referral-last-name">
                                    <span class="badge me-1">M0040</span> Last Name
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    id="Referral_Last_Name"
                                    name="referral-last-name"
                                    class="form-control"
                                    maxlength="50"
                                    placeholder="Enter Last Name"
                                    type="text"
                                    v-model="referral.Referral.LastName"
                                    @blur="v$.referral.Referral.LastName.$touch"
                                    :class="`${!v$.referral.Referral.LastName.$error ? '' : 'field-invalid'}`"
                                />
                                <span v-if="v$.referral.Referral.LastName.$error" class="field-invalid"
                                    >Lastname is required.</span
                                >
                            </div>
                            <div class="form-group col-12 col-lg-6 pe-lg-2">
                                <label id="Referral_Gender_Label" for="referral-gender">
                                    <span class="badge me-1">A0810</span> Sex
                                    <span class="text-danger">*</span>
                                </label>
                                <select
                                    id="Referral_Gender"
                                    class="form-select"
                                    name="referral-gender"
                                    v-model="referral.Referral.Gender"
                                    @blur="v$.referral.Referral.Gender.$touch"
                                    :class="`${!v$.referral.Referral.Gender.$error ? '' : 'field-invalid'}`"
                                >
                                    <option value="">Select Sex</option>
                                    <option
                                        v-for="(gender, index) of referralOptions.gender"
                                        :key="index"
                                        :value="gender.text"
                                    >
                                        {{ gender.text }}
                                    </option>
                                </select>
                                <span v-if="v$.referral.Referral.Gender.$error" class="field-invalid"
                                    >Sex is required.</span
                                >
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="date-of-birth">
                                    <span class="badge me-1">M0066</span> Date of Birth
                                    <span class="text-danger">*</span>
                                </label>
                                <DatePicker :getDate="onDateChange">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            name="Date-Of-Birth"
                                            type="text"
                                            class="form-control"
                                            placeholder="MM/DD/YYYY"
                                            :value="formatDateInput(referral.Referral.DOB)"
                                            @change="handleDateChange"
                                            autocomplete="off"
                                            @blur="v$.referral.Referral.DOB.$touch"
                                            :class="`${!v$.referral.Referral.DOB.$error ? '' : 'field-invalid'}`"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                        <span v-if="v$.referral.Referral.DOB.$error" class="field-invalid"
                                            >Date of Birth is required.</span
                                        >
                                    </div>
                                </DatePicker>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-social-security-number">
                                    <span class="badge me-1">M0064</span> Social Security Number
                                    <span class="text-danger">*</span>
                                </label>
                                <span>
                                    <div class="input-group d-flex w-100 flex-nowrap">
                                        <div class="flex-grow-1">
                                            <input
                                                name="referral-social-security-number"
                                                maxlength="9"
                                                class="form-control h-100"
                                                type="text"
                                                placeholder="Enter SSN"
                                                :disabled="ssnUnknown"
                                                v-model="ssnValue"
                                                v-number-only
                                                @blur="v$.referral.Referral.SSN.$touch"
                                                :class="`${!v$.referral.Referral.SSN.$error ? '' : 'field-invalid'}`"
                                            />
                                        </div>
                                        <div class="input-group-prepend flex">
                                            <div class="input-group-text form-check mb-0 rounded-0 py-1">
                                                <input
                                                    type="checkbox"
                                                    class="form-check-input mb-1 ms-1"
                                                    id="referral-ssn-na"
                                                    v-model="ssnUnknown"
                                                />
                                                <label class="form-check-label ms-2" for="referral-ssn-na">
                                                    <small> Unknown </small>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </span>
                                <span v-if="!ssnUnknown && v$.referral.Referral.SSN.$error">
                                    <span
                                        v-if="v$.referral.Referral.SSN.$errors[0].$message == 'Value is required'"
                                        class="field-invalid"
                                        >{{
                                            isDuplicateSSN
                                                ? "The SSN you entered is already assigned to another patient."
                                                : "Social Security Number is required."
                                        }}</span
                                    >
                                    <span v-else class="field-invalid">Please enter 9 digits.</span>
                                </span>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-marital-status"> Marital Status </label>
                                <select
                                    class="form-select"
                                    name="marital-status"
                                    v-model="referral.Referral.MaritalStatus"
                                >
                                    <option value="">Select Marital Status</option>
                                    <option :value="0">Select Marital Status</option>
                                    <option
                                        v-for="(status, index) in referralOptions.maritalStatus"
                                        :key="index"
                                        :value="status.value"
                                    >
                                        {{ status.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 py-2">
                        <div class="w-100">
                            <h6 class="mb-2 d-flex align-items-center">
                                <b class="me-2">Contact Information</b>
                            </h6>
                        </div>
                        <div class="row">
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-mobile-number">
                                    Mobile Phone
                                    <i
                                        class="fas fa-question-circle"
                                        v-tooltip="{
                                            title: `For emergency preparedness, patient will be contacted at this number. This will also populate to the emergency preparedness report.`,
                                            placement: 'right'
                                        }"
                                    ></i>
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    class="form-control phone-number-input"
                                    :value="formatPhone(referral.Referral.PhoneHome)"
                                    maxlength="14"
                                    placeholder="Enter Number"
                                    @input="(event) => setPhoneHome(event.target.value)"
                                    v-number-only
                                    @blur="v$.referral.Referral.PhoneHome.$touch"
                                    :class="`${!v$.referral.Referral.PhoneHome.$error ? '' : 'field-invalid'}`"
                                />
                                <span v-if="v$.referral.Referral.PhoneHome.$error">
                                    <span
                                        v-if="v$.referral.Referral.PhoneHome.$errors[0].$message == 'Value is required'"
                                        class="field-invalid"
                                        >Mobile Phone is required.</span
                                    >
                                    <span v-else class="field-invalid">10 Digit phone is required.</span>
                                </span>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-alternate-phone"> Alternate Phone </label>
                                <input
                                    type="text"
                                    class="form-control phone-number-input"
                                    :value="formatPhone(referral.Referral.PhoneMobile)"
                                    maxlength="14"
                                    placeholder="Enter Number"
                                    @input="(event) => setPhoneMobile(event.target.value)"
                                    v-number-only
                                />
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-email"> Email </label>
                                <input
                                    name="referral-email"
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter Email"
                                    v-model="referral.Referral.EmailAddress"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label id="Referral_Agency_Branch_Label" for="agency-location-id">
                                    Agency Branch
                                    <span class="text-danger">*</span>
                                </label>
                                <select
                                    id="Referral_Agency_Branch"
                                    name="agency-location-id"
                                    class="form-select"
                                    v-model="changeBranchId"
                                    @blur="v$.changeBranchId?.$touch"
                                    :class="`${!v$.changeBranchId?.$error ? '' : 'field-invalid'}`"
                                >
                                    <option value="">Select Branch</option>
                                    <option v-for="(branch, index) in branches" :key="index" :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <span v-if="v$.changeBranchId?.$error" class="field-invalid">Branch is required.</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-lg-6 px-4 col-md-12 g-0">
                    <div class="col-12 py-2">
                        <div class="w-100">
                            <h6 class="mb-2 d-flex align-items-center">
                                <b class="me-2">Patient Primary Address</b>
                            </h6>
                        </div>
                        <div class="row col-12">
                            <ReferralAddressForm
                                :referral="referral"
                                :address="addressTypes.primary"
                                :account-id="referral.accountId"
                                :application-id="referral.application"
                                :address-type="'primary'"
                                :show-code="true"
                                :county-data="primaryCounties"
                                :fields-required="true"
                                :fields-changed="addressFieldsChanged"
                                :referral-options="referralOptions"
                                :is-primary-address="true"
                                :is-refer="isRefer"
                                v-on:updatecountyData="updatecountyData"
                            />
                        </div>
                    </div>
                    <div class="col-12 py-2">
                        <div class="w-100" v-if="!hasVisitAddress">
                            <input
                                hidden
                                class="form-check-input"
                                type="radio"
                                v-model="hasVisitAddress"
                                name="visitaddrees"
                                id="visit-btn"
                                :value="true"
                            />
                            <label class="form-check-label cursor-pointer btn btn-xs btn-primary" for="visit-btn"
                                >+ ADD VISIT ADDRESS</label
                            >
                        </div>
                        <template v-if="hasVisitAddress">
                            <div class="w-90">
                                <h6 class="mb-2 float-start">
                                    <b class="me-2">Visit Address</b>
                                </h6>
                                <div class="mb-2 text-end">
                                    <input
                                        hidden
                                        class="form-check-input"
                                        type="radio"
                                        v-model="hasVisitAddress"
                                        name="visitaddrees"
                                        id="visit-btn"
                                        :value="false"
                                    />
                                    <label class="form-check-label cursor-pointer" for="visit-btn"
                                        ><i class="fas fa-times-circle"></i
                                    ></label>
                                </div>
                            </div>
                            <div class="row col-12">
                                <ReferralAddressForm
                                    :address="addressTypes.visit"
                                    :account-id="referral.accountId"
                                    :application-id="referral.application"
                                    :address-type="'visit'"
                                    :county-data="visitCounties"
                                    :show-code="false"
                                    :fields-changed="addressFieldsChanged"
                                    :referral-options="referralOptions"
                                    :is-refer="isRefer"
                                    v-on:updatecountyData="updatecountyData"
                                />
                            </div>
                        </template>
                    </div>
                    <div class="col-12 py-2">
                        <div class="w-100">
                            <h6 class="mb-2 d-flex align-items-center">
                                <b class="me-2">Patient Mailing Address</b>
                            </h6>
                            <div class="form-check custom-checkbox form-check-inline mb-2">
                                <input
                                    type="checkbox"
                                    id="sameprimarytomailing"
                                    class="form-check-input au-target"
                                    v-model="mailingMatchPrimary"
                                    @change="isMailingSameAsPrimaryChanged()"
                                />
                                <label class="form-check-label" for="sameprimarytomailing">
                                    <span class="me-1">Same as Primary Address</span>
                                </label>
                            </div>
                        </div>
                        <div class="row col-12">
                            <ReferralAddressForm
                                :address="addressTypes.mailing"
                                :application-id="referral.application"
                                :account-id="referral.accountId"
                                :address-type="'mailing'"
                                :show-code="false"
                                :county-data="mailingCounties"
                                :fields-required="true"
                                :fields-changed="addressFieldsChanged"
                                :referral-options="referralOptions"
                                :is-refer="isRefer"
                                v-on:updatecountyData="updatecountyData"
                            />
                        </div>
                        <div>
                            <button class="btn btn-xs btn-primary" @click="validateAddress()">Validate</button>
                        </div>
                    </div>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./DemographicsInformation.ts"></script>
